import { useCallback, useMemo, useState } from 'react'
import { Venue, VenueEvent } from 'types/model'

import { getId } from '@utils/strings'
import { getDaysInTheMonth, utcRange } from '@utils/date-time'
import { EventCalendarView } from '../views/event-calendar-view'
import { useVenueEvents } from '../data'
import { useVenue } from '@modules/venue/data'

export interface EventCalendarProps {
  stickyHeader?: boolean
  title?: string
  venue: Venue
  initialEvents?: VenueEvent[]
  initialMonth: number
  initialYear: number
  onCellClick: (event: VenueEvent) => void
  showMobileBottomPaginator?: boolean
}

export function EventCalendar({
  stickyHeader = true,
  title,
  venue: _venue,
  initialEvents,
  initialMonth,
  initialYear,
  onCellClick,
  showMobileBottomPaginator,
}: EventCalendarProps) {
  const venueId = useMemo(() => getId(_venue), [_venue])

  const { venue, events, month, year, handleMonthChange } =
    useEventCalendarController({
      venueId,
      initialVenue: _venue,
      initialMonth,
      initialYear,
      initialEvents,
    })

  return (
    <EventCalendarView
      onCellClick={onCellClick}
      title={title}
      stickyHeader={stickyHeader}
      venue={venue.data ?? _venue}
      events={events}
      month={month}
      year={year}
      loading={!venue.data || !events}
      onMonthChange={handleMonthChange}
      showMobileBottomPaginator={showMobileBottomPaginator}
    />
  )
}

function useEventCalendarController({
  venueId,
  initialMonth,
  initialYear,
  initialVenue,
  initialEvents,
}: {
  venueId: string
  initialMonth: number
  initialYear: number
  initialVenue?: Venue
  initialEvents?: VenueEvent[]
}) {
  const [month, setMonth] = useState<number>(initialMonth)
  const [year, setYear] = useState<number>(initialYear)

  const monthStart = useMemo(
    () => Date.UTC(year, month, 1).toString(),
    [month, year]
  )
  const monthEnd = useMemo(
    () => Date.UTC(year, month, getDaysInTheMonth(year, month)).toString(),
    [month, year]
  )

  const handleMonthChange = useCallback(
    (date: { month: number; year: number }) => {
      if (date.year !== year) setYear(date.year)
      if (date.month !== month) setMonth(date.month)
    },
    [month, year]
  )

  const venue = useVenue(venueId, { config: { fallbackData: initialVenue } })
  const purchaseWindow = venue.data?.inventoryTimeframe ?? 30

  const [_, windowEnd] = useMemo(
    () => utcRange(Date.now(), { days: purchaseWindow }).map(String),
    [purchaseWindow]
  )

  const { data: events, isValidating: validating } = useVenueEvents(venueId, {
    params: {
      dateStart: monthStart,
      dateEnd: monthEnd < windowEnd ? monthEnd : windowEnd,
      sort: 'date',
      limit: 50,
    },
  })

  return {
    venue,
    month,
    year,
    events,
    validating,
    handleMonthChange,
  }
}
